/**
 * CVO Settings View
 * Manages system settings and dropdown options
 * Self-contained view with its own API calls, state management, and dialogs
 */

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ConfirmDialog, SettingDialog } from '../../components/dialogs';
import {
  addSettingOption,
  updateSettingOption,
  deleteSettingOption,
  selectComplaintTypes,
  selectComplaintStatuses,
  selectComplaintPriorities,
  selectDepartments,
  selectOfficerDesignations,
  selectAuthorityPrivileges,
  selectCVOPrivileges,
  selectSettingsLoading,
  selectSettingsError,
} from '../../store/slices/settingsSlice';

const SettingsView = () => {
  const dispatch = useDispatch();
  
  // Redux state selectors
  const complaintTypesRaw = useSelector(selectComplaintTypes);
  const complaintStatusesRaw = useSelector(selectComplaintStatuses);
  const complaintPrioritiesRaw = useSelector(selectComplaintPriorities);
  const departmentsRaw = useSelector(selectDepartments);
  const officerDesignationsRaw = useSelector(selectOfficerDesignations);
  const authorityPrivilegesRaw = useSelector(selectAuthorityPrivileges);
  const cvoPrivilegesRaw = useSelector(selectCVOPrivileges);
  const settingsLoading = useSelector(selectSettingsLoading);
  const settingsError = useSelector(selectSettingsError);
  
  // Local state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, category: '', value: '', id: '' });
  const [addDialog, setAddDialog] = useState({ open: false, category: '', value: '' });
  const [editDialog, setEditDialog] = useState({ open: false, category: '', value: '', id: '' });
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // Filter only active items
  const filterActive = (items) => {
    if (!Array.isArray(items)) return [];
    return items.filter(item => {
      if (typeof item === 'string') return true;
      return item && 
             (!item.status || item.status === 'Active') && 
             !item.deleted_at;
    });
  };

  const complaintTypes = filterActive(complaintTypesRaw);
  const complaintStatuses = filterActive(complaintStatusesRaw);
  const complaintPriorities = filterActive(complaintPrioritiesRaw);
  const departments = filterActive(departmentsRaw);
  const officerDesignations = filterActive(officerDesignationsRaw);
  const authorityPrivileges = filterActive(authorityPrivilegesRaw);
  const cvoPrivileges = filterActive(cvoPrivilegesRaw);

  // Note: Settings data is loaded globally in CVODashboard on mount
  // This view only manages CRUD operations (add, update, delete)

  // Show notification helper
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  // Handle errors from Redux
  useEffect(() => {
    if (settingsError) {
      showNotification(settingsError, 'error');
    }
  }, [settingsError]);

  // Add new setting option
  const handleAddSettingOption = async (category, newOption) => {
    try {
      const result = await dispatch(addSettingOption({ 
        type: category, 
        data: { name: newOption } 
      }));
      
      if (addSettingOption.fulfilled.match(result)) {
        showNotification(`Successfully added ${newOption}`, 'success');
      } else {
        showNotification(`Failed to add ${newOption}`, 'error');
      }
    } catch (error) {
      showNotification(`Error: ${error.message}`, 'error');
    }
  };

  // Update setting option
  const handleUpdateSettingOption = async (category, itemId, updatedValue) => {
    try {
      const result = await dispatch(updateSettingOption({ 
        type: category, 
        id: itemId,
        data: { name: updatedValue } 
      }));
      
      if (updateSettingOption.fulfilled.match(result)) {
        showNotification(`Successfully updated to ${updatedValue}`, 'success');
      } else {
        showNotification(`Failed to update`, 'error');
      }
    } catch (error) {
      showNotification(`Error: ${error.message}`, 'error');
    }
  };

  // Remove setting option
  const handleRemoveSettingOption = async (category, optionToRemove, itemId) => {
    try {
      const id = itemId || optionToRemove;
      
      const result = await dispatch(deleteSettingOption({ 
        type: category, 
        id 
      }));
      
      if (deleteSettingOption.fulfilled.match(result)) {
        showNotification(`Successfully deleted ${optionToRemove}`, 'success');
      } else {
        showNotification(`Failed to delete ${optionToRemove}`, 'error');
      }
    } catch (error) {
      showNotification(`Error: ${error.message}`, 'error');
    }
  };
  // Helper function to get display name for category
  const getCategoryDisplayName = (category) => {
    const names = {
      complaintTypes: 'Complaint Types',
      complaintStatuses: 'Complaint Statuses',
      complaintPriorities: 'Priorities',
      officerDesignations: 'Officer Designations',
      departments: 'Departments',
      authorityPrivileges: 'Authority Privileges',
      cvoPrivileges: 'CVO Privileges'
    };
    return names[category] || category;
  };

  // Show confirmation dialog before removing any settings option
  const confirmAndRemove = (category, value, itemId) => {
    setConfirmDialog({ open: true, category, value, id: itemId || value });
  };

  // Render settings card
  const renderSettingCard = (title, items, category) => (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-md font-semibold text-gray-900">{title}</h3>
        <button
          onClick={() => setAddDialog({ open: true, category, value: '' })}
          className="inline-flex items-center px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          title={`Add ${title.toLowerCase()}`}
          disabled={settingsLoading}
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m6-6H6"/>
          </svg>
          Add
        </button>
      </div>
      <div className="grid grid-cols-1 gap-4">
        <div className="space-y-2">
          {items.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No {title.toLowerCase()} found</p>
          ) : (
            items.map((item, index) => {
              const itemName = typeof item === 'object' ? item.name : item;
              const itemId = typeof item === 'object' ? item.id : item;
              return (
                <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                  <span className="text-sm text-gray-700">{itemName}</span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setEditDialog({ open: true, category, value: itemName, id: itemId })}
                      className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                      disabled={settingsLoading}
                      title="Edit"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => confirmAndRemove(category, itemName, itemId)}
                      className="text-red-600 hover:text-red-800 disabled:opacity-50"
                      disabled={settingsLoading}
                      title="Delete"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="h-full flex flex-col">
        {/* Loading State */}
        {settingsLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            <span className="ml-3 text-gray-600">Loading settings...</span>
          </div>
        )}
        
        {/* Settings Grid */}
        {!settingsLoading && (
          <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 xl:grid-cols-2 gap-6">
            {renderSettingCard('Complaint Types', complaintTypes, 'complaintTypes')}
            {renderSettingCard('Complaint Statuses', complaintStatuses, 'complaintStatuses')}
            {renderSettingCard('Priorities', complaintPriorities, 'complaintPriorities')}
            {renderSettingCard('Officer Designations', officerDesignations, 'officerDesignations')}
            {renderSettingCard('Departments', departments, 'departments')}
            {renderSettingCard('Authority Privileges', authorityPrivileges, 'authorityPrivileges')}
            {renderSettingCard('CVO Privileges', cvoPrivileges, 'cvoPrivileges')}
          </div>
        )}

        {/* Confirm Delete Dialog */}
        <ConfirmDialog
          isOpen={confirmDialog.open}
          message={`Are you sure you want to delete "${confirmDialog.value}"?`}
          onConfirm={() => {
            handleRemoveSettingOption(confirmDialog.category, confirmDialog.value, confirmDialog.id);
            setConfirmDialog({ open: false, category: '', value: '', id: '' });
          }}
          onCancel={() => setConfirmDialog({ open: false, category: '', value: '', id: '' })}
          loading={settingsLoading}
        />

        {/* Add Item Dialog */}
        <SettingDialog
          isOpen={addDialog.open}
          mode="add"
          category={addDialog.category}
          value={addDialog.value}
          onValueChange={(value) => setAddDialog(prev => ({ ...prev, value }))}
          onSubmit={(value) => {
            handleAddSettingOption(addDialog.category, value);
            setAddDialog({ open: false, category: '', value: '' });
          }}
          onCancel={() => setAddDialog({ open: false, category: '', value: '' })}
          loading={settingsLoading}
        />

        {/* Edit Item Dialog */}
        <SettingDialog
          isOpen={editDialog.open}
          mode="edit"
          category={editDialog.category}
          value={editDialog.value}
          onValueChange={(value) => setEditDialog(prev => ({ ...prev, value }))}
          onSubmit={(value) => {
            handleUpdateSettingOption(editDialog.category, editDialog.id, value);
            setEditDialog({ open: false, category: '', value: '', id: '' });
          }}
          onCancel={() => setEditDialog({ open: false, category: '', value: '', id: '' })}
          loading={settingsLoading}
        />
      </div>

      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
          <div className={`rounded-lg shadow-xl p-4 flex items-center space-x-3 ${
            notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white`}>
            <div>
              {notification.type === 'success' ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <div className="flex-1">
              <p className="font-medium">{notification.message}</p>
            </div>
            <button
              onClick={() => setNotification({ show: false, message: '', type: 'success' })}
              className="text-white hover:text-gray-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default SettingsView;

